import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { policyPapersNews } from '../home/components/NewsSection';

// ─── Shared Reveal Logic ──────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll<HTMLElement>('[data-r]').forEach((node, i) => {
            setTimeout(() => {
              node.style.opacity = '1';
              node.style.transform = 'translateY(0)';
            }, i * 100);
          });
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

const hide = (): React.CSSProperties => ({
  opacity: 0,
  transform: 'translateY(24px)',
  transition: 'opacity 0.75s ease, transform 0.75s ease',
});

// ─── Full Article Card ────────────────────────────────────────────────────────
function ArticleCard({ item }: { item: (typeof policyPapersNews)[0] }) {
  return (
    <article className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300">
      {/* Image */}
      <div className="aspect-[16/8] overflow-hidden">
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover"
        />
      </div>

      <div className="p-7 md:p-10">
        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          {/* <span
            className="px-3 py-1 text-[11px] font-bold uppercase tracking-wider rounded-full"
            style={{ backgroundColor: 'rgba(0,119,182,0.08)', color: '#0077b6' }}
          >
            {item.featured && '★ '}
            {item.tag}
          </span> */}
          <span className="text-xs font-semibold text-gray-400">{item.date}</span>
        </div>

        {/* Title */}
        <h2
          className="text-2xl md:text-3xl font-black leading-tight mb-6"
          style={{ color: '#0F1C35', letterSpacing: '-0.02em' }}
        >
          {item.title}
        </h2>

        {/* Body paragraphs */}
        <div className="space-y-4">
          {item.body.map((para, i) => (
            <p
              key={i}
              className="text-base leading-relaxed"
              style={{ color: '#3D4A62' }}
            >
              {para}
            </p>
          ))}
        </div>

        {/* Source */}
        {item.source && (
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center gap-2">
            <svg
              className="w-4 h-4 flex-shrink-0"
              style={{ color: '#0077b6' }}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <span className="text-xs font-semibold text-gray-400 mr-1">Source:</span>
            <a
              href={item.source}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold break-all transition-colors hover:underline"
              style={{ color: '#0077b6' }}
            >
              {item.source}
            </a>
          </div>
        )}
      </div>
    </article>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PolicyPapersPage() {
  const location = useLocation();
  const articleId = (location.state as { articleId?: string } | null)?.articleId;
  const contentRef = useReveal();

  useEffect(() => {
    if (articleId) {
      // Navigate directly to the article — no top scroll
      const timer = setTimeout(() => {
        const el = document.getElementById(`article-${articleId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
      return () => clearTimeout(timer);
    } else {
      window.scrollTo(0, 0);
    }
  }, [articleId]);

  return (
    <div className="relative min-h-screen bg-white font-sans">
      <Navbar />

      <main className="pt-20">
        {/* ── Hero Banner ── (same style as MembershipTiersPage) */}
        <section className="bg-[#0F1C35] pt-32 pb-24 lg:pt-40 lg:pb-32 relative overflow-hidden">
          {/* decorative skew */}
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[#C9952A]/5 skew-x-12 translate-x-20 pointer-events-none" />

          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
            <div className="max-w-3xl mx-auto text-center flex flex-col items-center">
              
              <h1
                className="text-5xl md:text-5xl lg:text-6xl font-black text-[#0077b6] leading-tight tracking-tight"
              >
                Policy Papers
              </h1>
              <p className="mt-6 text-xl text-gray-300 font-light leading-relaxed">
                Analysis and perspectives on regulatory and policy <br /> developments relevant to Italy–Bangladesh relations.
              </p>
            </div>
          </div>
        </section>

        {/* ── Articles ── */}
        <section className="py-20 md:py-24 bg-[#F8F7F3]">
          <div
            ref={contentRef}
            className="max-w-[860px] mx-auto px-6 md:px-10"
          >
            {policyPapersNews.length === 0 ? (
              /* Empty state */
              <div className="text-center py-24">
                <div
                  className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: 'rgba(0,119,182,0.08)' }}
                >
                  <svg
                    className="w-7 h-7"
                    style={{ color: '#0077b6' }}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16" />
                    <path d="M3 21h18" />
                    <path d="M9 7h6M9 11h6M9 15h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-[#0F1C35] mb-2">No policy papers yet</h3>
                <p className="text-gray-400 text-sm">
                  New publications will appear here as they are released.
                </p>
              </div>
            ) : (
              <div className="space-y-10">
                {policyPapersNews.map((item) => (
                  <div
                    key={item.id}
                    id={`article-${item.id}`}
                    data-r
                    style={hide()}
                  >
                    <ArticleCard item={item} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}